import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Typography, Paper, Grid, List, ListItem, ListItemText, Divider, Alert } from '@mui/material';
import dayjs from 'dayjs'; // Import dayjs if used for date display
import { HotelFormData } from '@types';

const ReviewSubmitStep: React.FC = () => {
  // Get all current form values
  const { getValues } = useFormContext<HotelFormData>();
  const hotelData = getValues(); // Get latest values

  // Helper to display value or placeholder
  const displayValue = (value: any, placeholder = '-') => value || placeholder;
  const displayDate = (value: any, placeholder = '-') => (value ? dayjs(value).format('MM/DD/YYYY') : placeholder); // Format dates nicely

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" gutterBottom>
        Review Hotel Details
      </Typography>

      {/* Basic Info Review */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Basic Information
        </Typography>
        <Grid container spacing={1} rowSpacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Name:
            </Typography>
            <Typography>{displayValue(hotelData.name)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Property Type:
            </Typography>
            <Typography>{displayValue(hotelData.property_type)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Chain Affiliation:
            </Typography>
            <Typography>{displayValue(hotelData.chain_affiliation)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" display="block">
              Website:
            </Typography>
            <Typography sx={{ wordBreak: 'break-all' }}>{displayValue(hotelData.website)}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">
              Description:
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{displayValue(hotelData.description)}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Owner Info Review */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Owner Information
        </Typography>
        <Grid container spacing={1} rowSpacing={1.5}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Owner:
            </Typography>{' '}
            <Typography>{displayValue(hotelData.owner_name)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Entity:
            </Typography>{' '}
            <Typography>{displayValue(hotelData.owner_entity)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Legal Type:
            </Typography>{' '}
            <Typography>{displayValue(hotelData.legal_entity_type)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Ownership %:
            </Typography>{' '}
            <Typography>{displayValue(hotelData.ownership_percentage)}%</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Owner Since:
            </Typography>{' '}
            <Typography>{displayDate(hotelData.owner_since)}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Acquired:
            </Typography>{' '}
            <Typography>{displayDate(hotelData.acquisition_date)}</Typography>
          </Grid>
          {/* Add more owner fields as needed */}
        </Grid>
      </Paper>

      {/* Location Review */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Location
        </Typography>
        <Typography>
          {displayValue(hotelData.address)}, {displayValue(hotelData.city)}, {displayValue(hotelData.state)} {displayValue(hotelData.zip_code)}, {displayValue(hotelData.country)}
        </Typography>
      </Paper>

      {/* Specs Review */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Specifications
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Rooms:
            </Typography>{' '}
            <Typography>{displayValue(hotelData.num_rooms)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Rating:
            </Typography>{' '}
            <Typography>{displayValue(hotelData.star_rating)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Built:
            </Typography>{' '}
            <Typography>{displayValue(hotelData.year_built)}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="caption" color="text.secondary">
              Lot Size:
            </Typography>{' '}
            <Typography>{hotelData.lot_size ? `${hotelData.lot_size} sqft` : '-'}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Amenities/Renovations/Notes Review */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Amenities, Renovations & Notes
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Amenities:
        </Typography>
        <Typography sx={{ mb: 1 }}>{hotelData.amenities?.length > 0 ? hotelData.amenities.join(', ') : 'None specified'}</Typography>

        <Typography variant="caption" color="text.secondary" display="block">
          Renovations:
        </Typography>
        {hotelData.renovation_history?.length > 0 ? (
          <List dense disablePadding>
            {hotelData.renovation_history.map((r, i) => (
              <ListItem key={i} disableGutters>
                <ListItemText primary={`${r.year || 'N/A'}: ${r.description || 'No description'}`} secondary={`Cost: $${r.cost || 'N/A'}`} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography sx={{ mb: 1 }}>No entries</Typography>
        )}

        <Typography variant="caption" color="text.secondary" display="block">
          Notes:
        </Typography>
        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{displayValue(hotelData.notes)}</Typography>
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        Please review all information carefully. Click the "Submit Hotel" button to create this property entry.
      </Alert>
    </Box>
  );
};

export default ReviewSubmitStep;
